import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, CheckCircle2, Cloud, Link2Off, Play } from "lucide-react";
import { api, type ApiResponse } from "@/api/client";

interface GDriveStatus {
  connected: boolean;
  configured: boolean;
  email: string;
  folderName: string;
  lastBackupAt: string;
}

export default function IntegrationsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["gdrive-status"],
    queryFn: () => api.get<ApiResponse<GDriveStatus>>("/api/admin/integrations/gdrive/status"),
  });
  const status = data?.data;
  const [oauthMsg, setOauthMsg] = useState<{ kind: "error" | "config_error"; text: string } | null>(
    null,
  );

  // Refresh status when we return from the OAuth flow (?gdrive=connected|error|config_error).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("gdrive")) {
      const result = params.get("gdrive");
      if (result === "config_error") {
        setOauthMsg({
          kind: "config_error",
          text: "OAuth misconfigured: this API origin's redirect URI is not registered in the Google Cloud console.",
        });
      } else if (result === "error") {
        setOauthMsg({ kind: "error", text: "Could not connect Google Drive. Please try again." });
      }
      queryClient.invalidateQueries({ queryKey: ["gdrive-status"] });
      params.delete("gdrive");
      const qs = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
  }, [queryClient]);

  const connectMutation = useMutation({
    mutationFn: () =>
      api.get<ApiResponse<{ url: string }>>(
        `/api/admin/integrations/gdrive/connect?return=${encodeURIComponent(window.location.href)}`,
      ),
    onSuccess: (res) => {
      if (res.data?.url) window.location.href = res.data.url;
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => api.post("/api/admin/integrations/gdrive/disconnect", {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["gdrive-status"] }),
  });

  const [backupMsg, setBackupMsg] = useState<string | null>(null);
  const backupNowMutation = useMutation({
    mutationFn: () => api.post("/api/admin/integrations/gdrive/backup-now?kind=d1", {}),
    onSuccess: () => {
      setBackupMsg("Backup started — refresh in a minute to see 'Last backup' update.");
      // The export polls for up to ~60s; re-check status after a short delay.
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["gdrive-status"] }), 90_000);
    },
    onError: () => setBackupMsg("Could not start the backup. Please try again."),
  });

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Integrations</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">Connect external services</p>
      </div>

      {oauthMsg && (
        <div className="bg-error-50 text-error-700 border-error-200 rounded-lg border p-3 text-sm">
          {oauthMsg.text}
        </div>
      )}

      <section className="card space-y-4">
        <div className="flex items-center gap-3">
          <Cloud className="text-primary-500 h-6 w-6" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Google Drive backups
            </h2>
            <p className="text-sm text-gray-500">Weekly database + monthly storage backups.</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex h-20 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : !status?.configured ? (
          <p className="text-error-600 text-sm">
            Google OAuth client is not configured on the server (GDRIVE_CLIENT_ID /
            GDRIVE_CLIENT_SECRET secrets missing).
          </p>
        ) : status.connected ? (
          <div className="space-y-3">
            <div className="text-success-600 flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-5 w-5" />
              Connected{status.email ? ` as ${status.email}` : ""}
            </div>
            <dl className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <dt className="font-medium">Backup folder</dt>
                <dd>{status.folderName}</dd>
              </div>
              <div>
                <dt className="font-medium">Last backup</dt>
                <dd>
                  {status.lastBackupAt
                    ? new Date(status.lastBackupAt).toLocaleString()
                    : "No backup yet"}
                </dd>
              </div>
            </dl>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => {
                  setBackupMsg(null);
                  backupNowMutation.mutate();
                }}
                disabled={backupNowMutation.isPending}
                className="btn-secondary text-sm"
              >
                {backupNowMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Run database backup now
              </button>
              <button
                onClick={() => {
                  if (confirm("Disconnect Google Drive? Scheduled backups will stop."))
                    disconnectMutation.mutate();
                }}
                disabled={disconnectMutation.isPending}
                className="btn-ghost text-error-600 hover:bg-error-50 text-sm"
              >
                <Link2Off className="h-4 w-4" /> Disconnect
              </button>
            </div>
            {backupMsg && <p className="text-sm text-gray-500">{backupMsg}</p>}
          </div>
        ) : (
          <button
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
            className="btn-primary"
          >
            {connectMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Cloud className="h-4 w-4" />
            )}
            Connect Google Drive
          </button>
        )}
      </section>
    </div>
  );
}
